/**
 * API Client - Reusable HTTP Client with Auth Integration
 * გადატანადი API კლიენტი ავტორიზაციით
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiConfig, ApiError, PaginatedResponse, PaginationParams } from '../types';
import { getKeycloak } from '../auth/keycloak';

let apiInstance: AxiosInstance | null = null;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Create and configure API client
 */
export function createApiClient(config: ApiConfig): AxiosInstance {
  const instance = axios.create({
    baseURL: config.baseUrl,
    timeout: config.timeout || 30000,
    headers: {
      'Content-Type': 'application/json',
      ...config.headers,
    },
  });

  // Request interceptor - Add auth token
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const keycloak = getKeycloak();

      if (keycloak?.token) {
        // Check if token is about to expire (within 30 seconds)
        if (keycloak.isTokenExpired(30)) {
          try {
            // Prevent multiple refresh calls
            if (!refreshPromise) {
              refreshPromise = keycloak.updateToken(30).then(() => {
                refreshPromise = null;
                return keycloak.token || null;
              });
            }
            await refreshPromise;
          } catch {
            console.error('Failed to refresh token');
          }
        }

        config.headers.Authorization = `Bearer ${keycloak.token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - Handle errors
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const apiError = transformError(error);

      // Don't auto-redirect on 401 - let the calling code handle it
      // Auto-redirect causes loops when token is valid but API returns 401
      if (apiError.status === 401) {
        console.warn('API returned 401 Unauthorized');
      }

      return Promise.reject(apiError);
    }
  );

  apiInstance = instance;
  return instance;
}

/**
 * Get existing API instance
 */
export function getApiClient(): AxiosInstance {
  if (!apiInstance) {
    throw new Error('API client not initialized. Call createApiClient first.');
  }
  return apiInstance;
}

/**
 * Transform Axios error to ApiError
 */
function transformError(error: AxiosError): ApiError {
  if (error.response) {
    const data = error.response.data as Record<string, unknown>;
    return {
      status: error.response.status,
      message: (data.message as string) || (data.error as string) || 'An error occurred',
      code: (data.errorCode as string) || (data.code as string),
      details: (data.errors as Record<string, unknown>) || (data.details as Record<string, unknown>),
    };
  }

  if (error.request) {
    return {
      status: 0,
      message: 'Network error - please check your connection',
    };
  }

  return {
    status: 0,
    message: error.message || 'An unexpected error occurred',
  };
}

/**
 * Build pagination query params
 */
export function buildPaginationParams(params?: PaginationParams): Record<string, string> {
  const query: Record<string, string> = {};

  if (params?.page !== undefined) {
    query.page = String(params.page);
  }
  if (params?.size !== undefined) {
    query.size = String(params.size);
  }
  if (params?.sort) {
    query.sort = `${params.sort},${params.direction || 'asc'}`;
  }

  return query;
}

// ===== Convenience Methods =====

/**
 * GET request
 */
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await getApiClient().get<T>(url, config);
  return response.data;
}

/**
 * GET paginated request
 */
export async function getPaginated<T>(
  url: string,
  params?: PaginationParams,
  config?: AxiosRequestConfig
): Promise<PaginatedResponse<T>> {
  const queryParams = buildPaginationParams(params);
  const response = await getApiClient().get<PaginatedResponse<T>>(url, {
    ...config,
    params: { ...config?.params, ...queryParams },
  });
  return response.data;
}

/**
 * POST request
 */
export async function post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await getApiClient().post<T>(url, data, config);
  return response.data;
}

/**
 * PUT request
 */
export async function put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await getApiClient().put<T>(url, data, config);
  return response.data;
}

/**
 * PATCH request
 */
export async function patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await getApiClient().patch<T>(url, data, config);
  return response.data;
}

/**
 * DELETE request
 */
export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await getApiClient().delete<T>(url, config);
  return response.data;
}

/**
 * Upload file
 */
export async function upload<T>(
  url: string,
  file: File,
  fieldName = 'file',
  additionalData?: Record<string, string>,
  onProgress?: (progress: number) => void
): Promise<T> {
  const formData = new FormData();
  formData.append(fieldName, file);

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  const response = await getApiClient().post<T>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });

  return response.data;
}
